import secureStorage from "../../utils/secureStorage";
/**
 * BiometricSetupPrompt - Modal prompt untuk setup biometric login
 * Ditunjukkan pada:
 * - Login pertama di device baru
 * - First time user
 * - Selepas 7 hari jika user dismiss sebelum ini
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Fingerprint, ScanFace, X, ShieldCheck, Zap, Smartphone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import useBiometricAuth from "../../hooks/useBiometricAuth";
import { usePublicModules } from "../../hooks/useModuleAccess";

const PROMPT_COOLDOWN_DAYS = 7;
const STORAGE_KEY_DISMISSED = "biometricPromptDismissedAt";
const STORAGE_KEY_DEVICE_ID = "biometricDeviceId";

// Generate a unique device ID
const getOrCreateDeviceId = () => {
  let deviceId = localStorage.getItem(STORAGE_KEY_DEVICE_ID);
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(STORAGE_KEY_DEVICE_ID, deviceId);
  }
  return deviceId;
};

// Check if we should show the prompt
const shouldShowPrompt = (userId) => {
  // Check if biometric already enabled for this user on this device
  const storedUserId = secureStorage.getItem("biometricUserId");
  const biometricEnabled = secureStorage.getItem("biometricEnabled") === "true";
  
  if (biometricEnabled && storedUserId === userId) {
    return false; // Already set up
  }
  
  // Check cooldown (7 days)
  const dismissedAt = localStorage.getItem(STORAGE_KEY_DISMISSED);
  if (dismissedAt) {
    const dismissedDate = new Date(parseInt(dismissedAt, 10));
    const now = new Date();
    const daysSinceDismissed = (now - dismissedDate) / (1000 * 60 * 60 * 24);
    
    if (daysSinceDismissed < PROMPT_COOLDOWN_DAYS) {
      return false; // Still in cooldown
    }
  }
  
  return true;
};

export default function BiometricSetupPrompt({ user, onSetupComplete }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Kawalan module: biometric_login (berkuatkuasa semua user)
  const { isPublicModuleOn, loading: publicModulesLoading } = usePublicModules();
  const biometricLoginOn = isPublicModuleOn('biometric_login');
  
  const {
    isSupported,
    isEnabled,
    biometricType,
    isAndroid,
    registrationStatus,
    statusMessage,
    registerBiometric,
    cancelRegistration,
    resetStatus,
    isLoading: biometricLoading
  } = useBiometricAuth();

  // Check if we should show prompt on mount
  useEffect(() => {
    if (!user?.id || !isSupported || isEnabled || biometricLoading) {
      return;
    }
    
    // Module biometric_login OFF - jangan tunjuk prompt
    if (publicModulesLoading || !biometricLoginOn) {
      return;
    }
    
    // Small delay to let the page settle after login
    const timer = setTimeout(() => {
      if (shouldShowPrompt(user.id)) {
        setOpen(true);
      }
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [user?.id, isSupported, isEnabled, biometricLoading, publicModulesLoading, biometricLoginOn]);

  const handleActivate = async () => {
    setLoading(true);
    resetStatus();
    try {
      await registerBiometric(user.id, user.name || user.email || "User");
      toast.success(
        biometricType === "face" 
          ? "Face ID berjaya diaktifkan! 🎉" 
          : "Cap jari berjaya diaktifkan! 🎉"
      );
      setOpen(false);
      if (onSetupComplete) onSetupComplete();
    } catch (error) {
      console.error("Biometric setup error:", error);
      toast.error(error.message || "Gagal mengaktifkan biometric");
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    // Cancel any ongoing registration
    if (loading || registrationStatus !== 'idle') {
      cancelRegistration();
    }
    // Record dismissal time for 7-day cooldown
    localStorage.setItem(STORAGE_KEY_DISMISSED, Date.now().toString());
    setOpen(false);
  };

  // Don't render if not supported or module OFF
  if (!isSupported || !biometricLoginOn) {
    return null;
  }

  const BiometricIcon = biometricType === "face" ? ScanFace : Fingerprint;
  const getBiometricName = () => {
    if (biometricType === 'face') return 'Face ID';
    if (biometricType === 'biometric') return 'Biometrik'; // Android: fingerprint or face
    return 'Cap Jari';
  };
  const biometricName = getBiometricName();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm rounded-3xl border-0 p-0 overflow-hidden" hideCloseButton={true} data-testid="biometric-setup-modal">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-500 px-6 pt-6 pb-8 text-white relative">
          {/* Custom Close button - styled to match gradient header */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors z-10"
            data-testid="biometric-prompt-close"
          >
            <X className="w-4 h-4" />
          </button>
          
          {/* Icon */}
          <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BiometricIcon className="w-10 h-10" />
          </div>
          
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-center text-white">
              Aktifkan {biometricName}?
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-center text-white/90 text-sm mt-2">
            Log masuk lebih pantas dan selamat!
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          {/* Benefits */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-bold text-gray-800">Log Masuk Segera</p>
                <p className="text-sm text-gray-500">Tak perlu taip password lagi</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-bold text-gray-800">Lebih Selamat</p>
                <p className="text-sm text-gray-500">Hanya anda boleh akses akaun</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-bold text-gray-800">Khusus Peranti Ini</p>
                <p className="text-sm text-gray-500">Disimpan selamat dalam telefon</p>
              </div>
            </div>
          </div>

          {/* Registration Status UI */}
          {(loading || registrationStatus !== 'idle') && (
            <div className={`rounded-xl p-4 ${
              registrationStatus === 'error' 
                ? 'bg-red-50 border border-red-200' 
                : registrationStatus === 'waiting_user'
                ? 'bg-blue-50 border border-blue-200'
                : 'bg-emerald-50 border border-emerald-200'
            }`}>
              <div className="flex items-center gap-3">
                {registrationStatus === 'waiting_user' ? (
                  <>
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <BiometricIcon className="w-5 h-5 text-blue-600 animate-pulse" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-blue-800 text-sm">Menunggu Pengesahan</p>
                      <p className="text-xs text-blue-600 mt-0.5">
                        {statusMessage || (isAndroid 
                          ? 'Sila sentuh sensor cap jari atau tunjukkan wajah anda...'
                          : 'Sila gunakan Touch ID atau Face ID...')}
                      </p>
                    </div>
                  </>
                ) : registrationStatus === 'error' ? (
                  <>
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-red-800 text-sm">Gagal</p>
                      <p className="text-xs text-red-600 mt-0.5">{statusMessage || 'Sila cuba lagi'}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-emerald-800 text-sm">
                        {registrationStatus === 'requesting' ? 'Menghubungi Pelayan' : 'Memproses'}
                      </p>
                      <p className="text-xs text-emerald-600 mt-0.5">{statusMessage || 'Sila tunggu...'}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="space-y-2 pt-2">
            {registrationStatus === 'waiting_user' ? (
              <Button
                onClick={handleDismiss}
                variant="outline"
                className="w-full py-6 border-red-300 text-red-600 hover:bg-red-50 font-black text-base rounded-2xl"
                data-testid="biometric-cancel-btn"
              >
                <X className="w-5 h-5 mr-2" />
                Batal
              </Button>
            ) : (
              <Button
                onClick={handleActivate}
                disabled={loading || registrationStatus !== 'idle'}
                className="w-full py-6 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-black text-base rounded-2xl shadow-lg shadow-emerald-200/50"
                data-testid="biometric-activate-btn"
              >
                {loading && registrationStatus !== 'waiting_user' ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                    Mengaktifkan...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <BiometricIcon className="w-5 h-5" />
                    Aktifkan {biometricName}
                  </span>
                )}
              </Button>
            )}
            
            {registrationStatus !== 'waiting_user' && (
              <button
                onClick={handleDismiss}
                className="w-full py-3 text-gray-400 font-bold text-sm hover:text-gray-500 transition-colors"
                data-testid="biometric-later-btn"
              >
                Nanti dulu
              </button>
            )}
          </div>
          
          <p className="text-xs text-center text-gray-400">
            Anda boleh aktifkan kemudian di Tetapan Profil
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
